import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

type CreativeEditorStudioProps = {
  className?: string;
  eyebrow: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  status?: ReactNode;
  backAction: ReactNode;
  actions: ReactNode;
  warnings?: ReactNode;
  errors?: ReactNode;
  canvas: ReactNode;
  inspector: ReactNode;
};

export function CreativeEditorStudio({
  className,
  eyebrow,
  title,
  meta,
  status,
  backAction,
  actions,
  warnings,
  errors,
  canvas,
  inspector,
}: CreativeEditorStudioProps) {
  return (
    <div
      className={[
        "flex min-h-0 flex-col overflow-hidden bg-[#f5f3ec] text-foreground",
        className ?? "",
      ].join(" ")}
    >
      <header className="relative shrink-0 overflow-hidden border-b border-border/70 bg-[#fffdf7]/92 px-4 py-3 shadow-[0_18px_54px_rgba(19,34,49,0.08)] backdrop-blur-xl sm:px-6 lg:px-8 xl:px-10">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(circle at 74% 12%, rgba(255,241,212,0.52), transparent 28%), linear-gradient(90deg, rgba(19,34,49,0.035) 1px, transparent 1px)",
            backgroundSize: "auto, 92px 92px",
          }}
          aria-hidden="true"
        />
        <div className="relative flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground/58">
              {backAction}
            </div>
            <div className="flex min-w-0 flex-wrap items-end gap-3">
              <div className="min-w-0">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/52">
                  {eyebrow}
                </div>
                <h1 className="max-w-xl truncate font-display text-xl font-semibold tracking-[-0.035em] text-foreground sm:text-2xl">
                  {title}
                </h1>
              </div>
              {meta ? <span className="pb-1 text-xs text-foreground/45">{meta}</span> : null}
              {status ? (
                <span className="mb-0.5 rounded-full border border-warning-border bg-warning-surface px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-warning">
                  {status}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 xl:justify-end">{actions}</div>
        </div>
        {warnings ? <StudioNotice tone="warning">{warnings}</StudioNotice> : null}
        {errors ? <StudioNotice tone="danger">{errors}</StudioNotice> : null}
      </header>

      <div className="grid min-h-0 flex-1 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <section className="relative flex min-h-0 flex-col overflow-hidden bg-[#f1efe7]">
          <div
            className="pointer-events-none absolute inset-0 opacity-75"
            style={{
              background:
                "radial-gradient(circle at 50% 8%, rgba(200,255,0,0.16), transparent 22rem), radial-gradient(circle at 8% 88%, rgba(255,241,212,0.75), transparent 20rem), linear-gradient(rgba(19,34,49,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(19,34,49,0.035) 1px, transparent 1px)",
              backgroundSize: "auto, auto, 56px 56px, 56px 56px",
            }}
            aria-hidden="true"
          />
          <div className="relative flex min-h-0 flex-1 flex-col px-3 py-3 sm:px-5 lg:px-7 xl:px-8 xl:py-5">
            {canvas}
          </div>
        </section>

        <aside className="min-h-0 overflow-hidden border-t border-border/70 bg-[#f1efe7] p-3 text-foreground xl:border-l xl:border-t-0 xl:p-5">
          <div className="h-full overflow-hidden rounded-[2rem] border border-border/70 bg-[#fffdf8]">
            <div className="h-full overflow-y-auto px-4 pb-12 pt-4 xl:px-5">{inspector}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function StudioNotice({ children, tone }: { children: ReactNode; tone: "warning" | "danger" }) {
  const isDanger = tone === "danger";

  return (
    <div
      className={[
        "relative mt-3 flex items-start gap-2 rounded-2xl border px-3 py-2 text-xs leading-5 backdrop-blur",
        isDanger
          ? "border-destructive/25 bg-destructive/8 text-destructive"
          : "border-peach/70 bg-peach/50 text-peach-foreground",
      ].join(" ")}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
